import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Upload, Download, RefreshCw, X } from 'lucide-react';

const MasterLayout = ({
  title,
  endpoint,
  columns,
  formFields,
  searchFields = ['name', 'code'],
  allowBulkUpload = true,
  permissions = [],
  useSlidePanel = false,
  relatedEndpoints = {},   // { key: 'api-endpoint' } — fetched once on mount
  initFormData = null,     // (item) => formData — custom mapping when opening edit dialog
  transformSubmit = null,  // (formData) => submitData — transform before POST/PUT
}) => {
  const { api, hasRole, isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [relatedData, setRelatedData] = useState({});

  const canEdit = isAdmin() || hasRole(permissions);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/${endpoint}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api, endpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const keys = Object.keys(relatedEndpoints);
    if (keys.length === 0) return;
    const fetchRelated = async () => {
      const results = {};
      for (const key of keys) {
        try {
          const res = await api.get(`/${relatedEndpoints[key]}`);
          results[key] = res.data;
        } catch (e) {}
      }
      setRelatedData(results);
    };
    fetchRelated();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(initFormData ? initFormData(item) : item);
    } else {
      setEditingItem(null);
      setFormData(getDefaultFormData());
    }
    setIsDialogOpen(true);
  };

  const getDefaultFormData = () => {
    const defaults = {};
    formFields.forEach(field => {
      if (field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      } else if (field.type === 'boolean') {
        defaults[field.name] = true;
      } else if (field.type === 'number') {
        defaults[field.name] = '';
      } else {
        defaults[field.name] = '';
      }
    });
    return defaults;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required custom fields (Select/related-select don't trigger HTML5 required)
      for (const field of formFields) {
        if (field.required && (field.type === 'select' || field.type === 'related-select')) {
          const val = formData[field.name];
          if (!val || val === '') {
            toast.error(`${field.label} is required`);
            return;
          }
        }
      }

      // Prepare data - convert empty strings to null for optional fields
      let submitData = { ...formData };
      formFields.forEach(field => {
        if (submitData[field.name] === '' && !field.required) {
          submitData[field.name] = null;
        }
        if (field.type === 'number' && submitData[field.name] !== null) {
          submitData[field.name] = submitData[field.name] ? Number(submitData[field.name]) : null;
        }
      });
      if (transformSubmit) submitData = transformSubmit(submitData);

      if (editingItem) {
        await api.put(`/${endpoint}/${editingItem.id}`, submitData);
        toast.success('Updated successfully');
      } else {
        await api.post(`/${endpoint}`, submitData);
        toast.success('Created successfully');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response && error.response.data && error.response.data.detail;
      const msg = typeof detail === 'string' ? detail : 'Operation failed';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.detail) || 'Delete failed';
      toast.error(msg);
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await api.post(`/bulk-upload/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      setIsUploadDialogOpen(false);
      setUploadFile(null);
      fetchData();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.detail) || 'Upload failed';
      toast.error(msg);
    }
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return searchFields.some(field => {
      const val = item[field];
      return val && val.toString().toLowerCase().includes(term);
    });
  });

  const renderFormField = (field) => {
    const value = formData[field.name] !== undefined && formData[field.name] !== null ? formData[field.name] : '';

    if (field.type === 'related-select') {
      const options = (relatedData[field.relatedKey] || []).filter(o => o.is_active !== false);
      return (
        <Select
          value={value ? value.toString() : ''}
          onValueChange={(val) => setFormData(prev => ({ ...prev, [field.name]: val }))}
        >
          <SelectTrigger className="rounded-sm">
            <SelectValue placeholder={`Select ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.id} value={opt.id.toString()}>
                {typeof field.optionLabel === 'function' ? field.optionLabel(opt) : opt[field.optionLabel || 'name']}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.type === 'select') {
      const options = (field.options || []).filter(function(opt) { return opt.value !== '' && opt.value !== null && opt.value !== undefined; });
      return (
        <Select
          value={value ? value.toString() : ''}
          onValueChange={(val) => setFormData(prev => ({ ...prev, [field.name]: val }))}
        >
          <SelectTrigger className="rounded-sm">
            <SelectValue placeholder={`Select ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value.toString()}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.type === 'boolean') {
      return (
        <Select
          value={value !== undefined && value !== null ? value.toString() : 'true'}
          onValueChange={(val) => setFormData(prev => ({ ...prev, [field.name]: val === 'true' }))}
        >
          <SelectTrigger className="rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => { const v = e.target.value; setFormData(prev => ({ ...prev, [field.name]: v })); }}
          className="form-input min-h-[80px]"
          placeholder={field.placeholder}
          required={field.required}
        />
      );
    }

    return (
      <Input
        type={field.type || 'text'}
        value={value}
        onChange={(e) => { const v = e.target.value; setFormData(prev => ({ ...prev, [field.name]: v })); }}
        placeholder={field.placeholder}
        required={field.required}
        className="rounded-sm"
      />
    );
  };

  const renderCellValue = (item, column) => {
    const value = item[column.key];
    
    if (column.render) {
      return column.render(value, item);
    }
    
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') {
      return value ? (
        <span className="badge-success">Active</span>
      ) : (
        <span className="badge-danger">Inactive</span>
      );
    }
    if (column.key.includes('date') || column.key.includes('_at')) {
      return value ? new Date(value).toLocaleDateString() : '-';
    }
    
    return value;
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid={`master-${endpoint}`}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {allowBulkUpload && (
              <Button
                variant="outline"
                onClick={() => setIsUploadDialogOpen(true)}
                className="rounded-sm"
                data-testid="bulk-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
            )}
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="add-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchData}
              className="rounded-sm"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No records found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {columns.map((col) => (
                      <TableHead key={col.key} className="font-semibold text-slate-700">
                        {col.label}
                      </TableHead>
                    ))}
                    {canEdit && (
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      {columns.map((col) => (
                        <TableCell key={col.key} className="text-sm">
                          {renderCellValue(item, col)}
                        </TableCell>
                      ))}
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(item)}
                              className="h-8 w-8 text-slate-600 hover:text-sky-600"
                              data-testid={`edit-${item.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(item)}
                              className="h-8 w-8 text-slate-600 hover:text-red-600"
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit - Slide Panel or Dialog */}
      {useSlidePanel ? (
        isDialogOpen && (
          <div className="fixed top-0 right-0 h-screen w-[500px] bg-white shadow-2xl z-50 border-l border-slate-200 flex flex-col animate-slide-in-right" data-testid="slide-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-semibold text-lg">{editingItem ? `Edit ${title.replace(/s$/, '')}` : `Add ${title.replace(/s$/, '')}`}</h2>
              <Button variant="ghost" size="icon" onClick={() => setIsDialogOpen(false)} className="h-8 w-8"><X className="w-4 h-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formFields.map((field) => (
                  <div key={field.name} className={field.fullWidth ? 'md:col-span-2' : ''}>
                    <Label className="form-label">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderFormField(field)}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-form">
                  {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        )
      ) : (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? `Edit ${title.replace(/s$/, '')}` : `Add New ${title.replace(/s$/, '')}`}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                {formFields.map((field) => (
                  <div key={field.name} className={field.fullWidth ? 'md:col-span-2' : ''}>
                    <Label className="form-label">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderFormField(field)}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-form">
                  {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload {title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="form-label">Excel File (.xlsx)</Label>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setUploadFile(e.target.files[0])}
              className="rounded-sm"
            />
            <p className="text-xs text-slate-500 mt-2">
              Upload an Excel file with columns matching the required fields.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUploadDialogOpen(false);
                setUploadFile(null);
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpload}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterLayout;
